import { logPendingNav } from "src/utils/logPendingNav";

export type EntryIdType = "lbeid" | "partid" | "meid";

interface PendingRequestBody {
  request?: string;
  params?: {
    lbid?: string;
    lbeid?: string;
    partid?: string;
    meid?: string;
    rid?: string;
    list?: Array<{
      lbeid?: string | null;
      partid?: string | null;
      meid?: string | null;
    }>;
  };
  meid?: string;
  partid?: string;
  lbeid?: string;
}

const parseLbidFromEntryId = (entryId: string) => {
  const lbidPart = entryId.split("&&").find(part => part.startsWith("lbid="));
  return lbidPart ? lbidPart.slice(5) : undefined;
};

const parseReqParts = (req: string) => {
  const [request, entryId] = req.split("__");
  return { request, entryId };
};

const resolveLbid = (
  body: PendingRequestBody,
  ...entryIds: Array<string | undefined>
) => {
  if (body.params?.lbid) {
    return body.params.lbid;
  }

  for (const entryId of entryIds) {
    const lbid = parseLbidFromEntryId(entryId ?? "");
    if (lbid) {
      return lbid;
    }
  }

  return undefined;
};

const resolveIdType = (
  entryId: string,
  body: PendingRequestBody,
): EntryIdType | null => {
  if (body.params?.meid || body.meid || entryId.includes("meid=")) {
    return "meid";
  }
  if (body.params?.partid || body.partid) {
    return "partid";
  }
  if (body.params?.lbeid || body.lbeid) {
    return "lbeid";
  }
  return null;
};

export const inferIdTypeFromBody = (
  body: PendingRequestBody | Record<string, unknown>,
): EntryIdType => {
  const params = (body as PendingRequestBody).params ?? {};
  const typedBody = body as PendingRequestBody;
  if (params.meid || typedBody.meid) {
    return "meid";
  }
  if (params.partid || typedBody.partid) {
    return "partid";
  }
  return "lbeid";
};

export const indexEntryIdTypes = (
  rows: Array<{
    req: string;
    data: string;
    server_entry_id?: string | null;
    local_entry_id?: string | null;
  }>,
) => {
  const byKey = new Map<string, EntryIdType>();

  const remember = (key: string | null | undefined, idType: EntryIdType) => {
    if (key) {
      byKey.set(key, idType);
    }
  };

  rows.forEach(row => {
    const request = row.req.split("__")[0];
    if (request !== "save-entry" && request !== "withdraw-entry") {
      return;
    }

    let body: PendingRequestBody;
    try {
      body = JSON.parse(row.data);
    } catch {
      return;
    }

    const idType = inferIdTypeFromBody(body);
    const entryId = row.req.split("__")[1];
    const params = body.params ?? {};

    remember(row.local_entry_id, idType);
    remember(row.server_entry_id, idType);
    remember(entryId, idType);
    remember(params.lbeid, idType);
    remember(params.partid, idType);
    remember(params.meid, idType);
  });

  return byKey;
};

export const lookupEntryIdType = (
  byKey: Map<string, EntryIdType> | undefined,
  ...keys: Array<string | null | undefined>
): EntryIdType | undefined => {
  if (!byKey) {
    return undefined;
  }
  for (const key of keys) {
    if (!key) {
      continue;
    }
    const match = byKey.get(key);
    if (match) {
      return match;
    }
  }
  return undefined;
};

const resolveRouteIdType = (
  body: PendingRequestBody,
  entryIdTypeByKey: Map<string, EntryIdType> | undefined,
  resolvedEntryId: string,
  ...lookupKeys: Array<string | null | undefined>
): EntryIdType | null =>
  lookupEntryIdType(entryIdTypeByKey, resolvedEntryId, ...lookupKeys) ??
  resolveIdType(resolvedEntryId, body) ??
  (resolvedEntryId ? "lbeid" : null);

const isPlaceholderEntryId = (id?: string | null) =>
  typeof id === "string" && id.includes("new=");

const resolveNavEntryId = (
  entryId: string,
  serverEntryId?: string | null,
) =>
  isPlaceholderEntryId(entryId) && serverEntryId ? serverEntryId : entryId;

const buildLogbookRoute = (
  lbid: string,
  idType: EntryIdType,
  entryId: string,
) => `/logbook/${lbid}/${idType}/${entryId}`;

export const getPendingRequestRoute = (
  pending: {
    req: string;
    data: string;
    server_entry_id?: string | null;
    local_entry_id?: string | null;
    entryIdTypeByKey?: Map<string, EntryIdType>;
  },
): string | null => {
  let body: PendingRequestBody;
  try {
    body = JSON.parse(pending.data);
  } catch (error) {
    logPendingNav("route: failed to parse upload body", {
      req: pending.req,
      error,
    });
    return null;
  }

  const { request, entryId } = parseReqParts(pending.req);
  if (!request || !entryId) {
    logPendingNav("route: missing request or entry id in req", {
      req: pending.req,
      request,
      entryId,
    });
    return null;
  }

  const logRoute = (
    route: string | null,
    details: Record<string, unknown>,
  ) => {
    if (request === "checklist-action" || !route) {
      logPendingNav(`route: ${request}`, {
        req: pending.req,
        server_entry_id: pending.server_entry_id,
        apiRequest: body.request,
        params: body.params,
        route,
        ...details,
      });
    }
    return route;
  };

  if (request === "save-entry" || request === "withdraw-entry") {
    const resolvedEntryId = resolveNavEntryId(
      entryId,
      pending.server_entry_id,
    );
    const lbid = resolveLbid(body, entryId);
    const idType = resolveRouteIdType(
      body,
      pending.entryIdTypeByKey,
      resolvedEntryId,
      entryId,
      pending.local_entry_id,
      pending.server_entry_id,
    );
    if (!lbid || !idType) {
      return logRoute(null, { entryId, resolvedEntryId, lbid, idType });
    }
    return logRoute(buildLogbookRoute(lbid, idType, resolvedEntryId), {
      entryId,
      resolvedEntryId,
      lbid,
      idType,
    });
  }

  if (request === "upload-file") {
    const rawEntryId = body.meid ?? body.partid ?? body.lbeid ?? entryId;
    const resolvedEntryId = resolveNavEntryId(
      rawEntryId,
      pending.server_entry_id,
    );
    const lbid = resolveLbid(body, rawEntryId, entryId);
    const idType = resolveRouteIdType(
      body,
      pending.entryIdTypeByKey,
      resolvedEntryId,
      rawEntryId,
      entryId,
      pending.local_entry_id,
      pending.server_entry_id,
    );
    if (!lbid || !idType || !resolvedEntryId) {
      return logRoute(null, { rawEntryId, resolvedEntryId, lbid, idType });
    }
    return logRoute(buildLogbookRoute(lbid, idType, resolvedEntryId), {
      rawEntryId,
      resolvedEntryId,
      lbid,
      idType,
    });
  }

  if (request === "checklist-action") {
    const rawEntryId = body.params?.rid ?? entryId;
    const resolvedEntryId = resolveNavEntryId(
      rawEntryId,
      pending.server_entry_id,
    );
    const lbid = resolveLbid(body, rawEntryId, entryId);
    const idType = resolveRouteIdType(
      body,
      pending.entryIdTypeByKey,
      resolvedEntryId,
      rawEntryId,
      entryId,
      pending.local_entry_id,
      pending.server_entry_id,
    );
    if (!lbid || !idType || !resolvedEntryId) {
      return logRoute(null, {
        reqEntryId: entryId,
        rawEntryId,
        resolvedEntryId,
        lbid,
        idType,
      });
    }
    return logRoute(buildLogbookRoute(lbid, idType, resolvedEntryId), {
      reqEntryId: entryId,
      rawEntryId,
      resolvedEntryId,
      lbid,
      idType,
    });
  }

  if (request === "verify-entry") {
    const item = body.params?.list?.[0];
    if (!item) {
      return null;
    }
    if (item.meid) {
      return `/summary/verify-entries/meid/${item.meid}`;
    }
    if (item.partid) {
      return `/summary/verify-entries/partid/${item.partid}`;
    }
    if (item.lbeid) {
      return `/summary/verify-entries/lbeid/${item.lbeid}`;
    }
  }

  return null;
};
